import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Trash2, Pin, Megaphone, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from "@tabipla/ui";
import {
  useAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useToggleReaction,
} from "@/application/hooks/useAnnouncements";
import { useAuth } from "@/application/hooks/useAuth";
import type { AnnouncementResponseDto, AddReactionDto } from "@tabipla/shared";

interface AnnouncementPanelProps {
  groupId: string;
  isOwner: boolean;
}

const REACTION_EMOJIS: AddReactionDto["emoji"][] = ["👍", "✅", "❓", "🎉"];

export function AnnouncementPanel({ groupId, isOwner }: AnnouncementPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [newContent, setNewContent] = useState("");

  const { data: announcements, isLoading } = useAnnouncements(groupId);
  const createMutation = useCreateAnnouncement(groupId);
  const updateMutation = useUpdateAnnouncement(groupId);
  const deleteMutation = useDeleteAnnouncement(groupId);
  const reactionMutation = useToggleReaction(groupId);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim()) return;

    try {
      await createMutation.mutateAsync({ content: newContent.trim() });
      setNewContent("");
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      // Error handled by mutation
    }
  };

  const handleTogglePin = async (announcement: AnnouncementResponseDto) => {
    try {
      await updateMutation.mutateAsync({
        announcementId: announcement.id,
        data: { isPinned: !announcement.isPinned },
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleReaction = async (announcementId: string, emoji: AddReactionDto["emoji"]) => {
    try {
      await reactionMutation.mutateAsync({
        announcementId,
        data: { emoji },
      });
    } catch {
      // Error handled by mutation
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t("announcements.justNow");
    if (diffMins < 60) return t("announcements.minutesAgo", { count: diffMins });
    if (diffHours < 24) return t("announcements.hoursAgo", { count: diffHours });
    if (diffDays < 7) return t("announcements.daysAgo", { count: diffDays });

    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
    });
  };

  const isOwnAnnouncement = (announcement: AnnouncementResponseDto) => {
    return announcement.authorId === user?.id;
  };

  const hasReacted = (announcement: AnnouncementResponseDto, emoji: string) => {
    const reaction = announcement.reactions.find((r) => r.emoji === emoji);
    return reaction?.reactorIds.includes(user?.id ?? "") ?? false;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Megaphone className="h-5 w-5" />
          {t("announcements.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New announcement input */}
        <form onSubmit={handleCreate} className="space-y-2">
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder={t("announcements.placeholder")}
            disabled={createMutation.isPending}
            className="min-h-[80px] w-full resize-none rounded-lg border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={!newContent.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t("announcements.post")}
            </Button>
          </div>
        </form>

        {/* Announcements list */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : announcements?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <Megaphone className="mb-2 h-8 w-8 opacity-50" />
              <p className="text-sm">{t("announcements.noAnnouncements")}</p>
              <p className="text-xs">{t("announcements.startSharing")}</p>
            </div>
          ) : (
            announcements?.map((announcement) => (
              <div
                key={announcement.id}
                className={`rounded-lg border p-4 ${
                  announcement.isPinned ? "border-primary/30 bg-primary/5" : "bg-background"
                }`}
              >
                {/* Header */}
                <div className="mb-2 flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {announcement.isPinned && <Pin className="h-3.5 w-3.5 text-primary" />}
                    <span className="text-sm font-medium">{announcement.authorName}</span>
                    {announcement.authorType === "guest" && (
                      <Badge variant="outline" className="px-1 py-0 text-[10px]">
                        {t("groups.guest")}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatTime(announcement.createdAt)}
                    </span>
                  </div>
                  {(isOwnAnnouncement(announcement) || isOwner) && (
                    <div className="flex items-center gap-1">
                      {isOwner && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleTogglePin(announcement)}
                          title={
                            announcement.isPinned
                              ? t("announcements.unpin")
                              : t("announcements.pin")
                          }
                        >
                          <Pin
                            className={`h-3.5 w-3.5 ${announcement.isPinned ? "text-primary" : ""}`}
                          />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(announcement.id)}
                        title={t("common.delete")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Content */}
                <p className="mb-3 whitespace-pre-wrap text-sm">{announcement.content}</p>

                {/* Reactions */}
                <div className="flex flex-wrap items-center gap-2">
                  {REACTION_EMOJIS.map((emoji) => {
                    const reaction = announcement.reactions.find((r) => r.emoji === emoji);
                    const count = reaction?.count ?? 0;
                    const reacted = hasReacted(announcement, emoji);

                    return (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(announcement.id, emoji)}
                        disabled={reactionMutation.isPending}
                        className={`flex items-center gap-1 rounded-full border px-2 py-1 text-sm transition-colors ${
                          reacted
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-muted hover:border-primary/50 hover:bg-muted"
                        }`}
                      >
                        <span>{emoji}</span>
                        {count > 0 && <span className="text-xs">{count}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
